import { useCallback } from 'react';

/**
 * Column definition for XLSX export.
 * Matches the shape used by DataTable and useCsvExport.
 */
export interface XlsxColumn {
  key: string;
  header: string;
}

/**
 * React hook that provides an `exportXlsx` function for client-side XLSX export.
 *
 * exceljs (~95 KB) is loaded **dynamically** only when the user clicks export,
 * keeping it out of the initial JavaScript bundle.
 *
 * Features:
 * - Bold headers with gray background
 * - Auto-filter on header row
 * - Auto column widths based on header length
 * - Number columns aligned right
 * - Downloads as .xlsx Blob via temporary anchor element
 *
 * @example
 * const { exportXlsx, loading } = useXlsxExport();
 * <button onClick={() => exportXlsx(columns, rows, 'Marcas')}>Exportar XLSX</button>
 */
export function useXlsxExport() {
  const exportXlsx = useCallback(
    async <T extends Record<string, any>>(
      columns: XlsxColumn[],
      rows: T[],
      filename: string,
    ): Promise<void> => {
      if (!columns.length) {
        console.warn('[useXlsxExport] No columns provided, skipping export.');
        return;
      }

      try {
        // Dynamic import — exceljs is NOT in the initial bundle
        const ExcelJS = await import('exceljs');

        const workbook = new ExcelJS.Workbook();
        const sheet = workbook.addWorksheet(filename.slice(0, 31) || 'Datos');

        // Define columns (needed for auto-filter and width)
        sheet.columns = columns.map((col) => ({
          header: col.header,
          key: col.key,
        }));

        // Style header row (row 1)
        const headerRow = sheet.getRow(1);
        headerRow.font = { bold: true };
        headerRow.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FFD9D9D9' },
        };

        // Add data rows
        for (const row of rows) {
          const rowValues = columns.map((col) => {
            const val = row[col.key];
            return val === null || val === undefined ? '' : val;
          });
          sheet.addRow(rowValues);
        }

        // Auto column widths based on header length (approximate)
        sheet.columns.forEach((col, idx) => {
          const headerLen = String(col.header ?? columns[idx]?.header ?? '').length;
          sheet.getColumn(idx + 1).width = Math.max(headerLen * 1.2 + 3, 10);
        });

        // Auto-filter on header row
        if (rows.length > 0) {
          sheet.autoFilter = {
            from: { row: 1, column: 1 },
            to: { row: rows.length + 1, column: columns.length },
          };
        }

        // Generate buffer and trigger download
        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        const dateStr = new Date().toISOString().slice(0, 10);
        a.download = `${filename.replace(/\.xlsx$/i, '')}-${dateStr}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } catch (err) {
        console.error('[useXlsxExport] Failed to generate XLSX:', err);
        throw err;
      }
    },
    [],
  );

  return { exportXlsx };
}
